import type { FormEvent, ReactNode } from 'react';
import { X } from 'lucide-react';
import { Form } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { SheetFrame, SheetFrameBody, SheetFrameFooter, SheetFrameHeading } from './SheetFrame';

interface SheetProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Прибита под прокручиваемым содержимым — для главного действия шторки. */
  footer?: ReactNode;
  /**
   * Есть — тело и футер оборачиваются в HeroUI `Form` (`display: contents`,
   * в разметку не вмешивается): submit-кнопка футера и Enter в поле реально
   * сабмитят форму, и `isRequired`-поля показывают `FieldError` сами, без
   * ручной проверки на каждый чих. Нет — как раньше, для не-форм шторок
   * (например, меню действий со списком).
   */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}

/**
 * Единая шторка снизу — на HeroUI `Drawer` (на компьютере в браузере — окно
 * по центру, см. `SheetFrame`) (свайп вниз, drag-хэндл, esc,
 * тап по фону — всё уже есть в примитиве react-aria, бесплатно), но с
 * шапкой в стиле старого `shared/ui/BottomSheet` (заголовок слева, крестик
 * кружком справа, в одну строку, не «крестик в углу» дефолтного
 * `Drawer.CloseTrigger`/`Drawer.Header`) — так выглядели все шторки в
 * приложении до того, как страница «Словарь» перешла на HeroUI, и по
 * решению Дмитрия все шторки должны выглядеть одинаково, а не по-разному
 * в разных разделах.
 *
 * Раньше в приложении было два разных компонента шторки — этот (`Sheet`,
 * жил в `pages/dictionaries/ui`, на `Drawer`, с свайпом) и `BottomSheet`
 * (ручная реализация порталом в `document.body`, без свайпа — баг, из-за
 * которого шторку меню карточки «Учить»/«Повторять» нельзя было смахнуть
 * вниз). Оба заменены этим единственным компонентом, как `PageLayout` —
 * общий каркас страницы, теперь есть и общий каркас шторки.
 *
 * Шапка собрана вручную (`Drawer.Heading` + свой `button`), а не через
 * `Drawer.Header`/`Drawer.CloseTrigger`: у HeroUI `.drawer__close-trigger`
 * спозиционирован `absolute` в углу диалога, вне потока — не подходит под
 * стиль «крестик рядом с заголовком в одну строку», и `.drawer__header` по
 * умолчанию `flex-col` (заголовок + место под доп. контент друг под
 * другом), а не `flex-row`.
 */
export function Sheet({ title, onClose, children, footer, onSubmit }: SheetProps) {
  const { t } = useTranslation('Sheet');
  const body = <SheetFrameBody>{children}</SheetFrameBody>;
  const footerSlot = footer != null && <SheetFrameFooter className="flex-col">{footer}</SheetFrameFooter>;

  return (
    <SheetFrame onClose={onClose} dialogClassName="pb-[calc(var(--safe-bottom)+0.75rem)]">
      {/* Больше воздуха над заголовком, чем даёт голый `Drawer.Handle` —
          иначе заголовок стоит вплотную к хэндлу, тесно. */}
      <div className="flex shrink-0 items-center justify-between pt-4 pb-2">
        <SheetFrameHeading className="text-lg font-bold text-ink">{title}</SheetFrameHeading>
        <button
          type="button"
          aria-label={t('close')}
          onClick={onClose}
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-glass text-ink active:bg-glass-2"
        >
          <X className="size-5" aria-hidden="true" />
        </button>
      </div>
      {onSubmit ? (
        <Form onSubmit={onSubmit} className="contents">
          {body}
          {footerSlot}
        </Form>
      ) : (
        <>
          {body}
          {footerSlot}
        </>
      )}
    </SheetFrame>
  );
}
